// Hinweise (Toasts) und Schutzabfragen – statt alert()/confirm() (im Altsystem 194 Stellen).
import { useMutationState } from "@tanstack/react-query";
import { CircleAlert, CircleCheck, Info, LoaderCircle, X } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

type HinweisArt = "gut" | "kritisch" | "info";
type Hinweis = { id: number; art: HinweisArt; titel: string; text?: string; aktion?: { text: string; los: () => void } };

export type Schutzabfrage = {
  titel: string;
  text: ReactNode;
  /** wiederholt das Verb mit Zahl, z. B. „12 verschieben“ – nie „OK“ */
  bestaetigen: string;
  gefahr?: boolean;
  /** Zeile der Paritätsmatrix (SA-…), die diese Abfrage ersetzt */
  ersetzt?: string;
};

type Rueckmeldung = {
  melden: (h: Omit<Hinweis, "id">) => void;
  bestaetigen: (a: Schutzabfrage) => Promise<boolean>;
};

const Kontext = createContext<Rueckmeldung | null>(null);

const SYMBOL = { gut: CircleCheck, kritisch: CircleAlert, info: Info } as const;

export function RueckmeldungProvider({ children }: { children: ReactNode }) {
  const [hinweise, setHinweise] = useState<Hinweis[]>([]);
  const [abfrage, setAbfrage] = useState<(Schutzabfrage & { antwort: (ja: boolean) => void }) | null>(null);
  const zaehler = useRef(0);

  const weg = useCallback((id: number) => setHinweise((h) => h.filter((x) => x.id !== id)), []);

  const melden = useCallback((h: Omit<Hinweis, "id">) => {
    const id = ++zaehler.current;
    setHinweise((alt) => [...alt.slice(-3), { ...h, id }]);
    if (h.art !== "kritisch") window.setTimeout(() => weg(id), h.aktion ? 8000 : 5000);
  }, [weg]);

  const bestaetigen = useCallback((a: Schutzabfrage) => new Promise<boolean>((fertig) => {
    setAbfrage({ ...a, antwort: (ja) => { setAbfrage(null); fertig(ja); } });
  }), []);

  const wert = useMemo(() => ({ melden, bestaetigen }), [melden, bestaetigen]);

  return (
    <Kontext.Provider value={wert}>
      {children}
      <div className="im-toasts" aria-live="polite">
        <Arbeit />
        {hinweise.map((h) => {
          const Icon = SYMBOL[h.art];
          return (
            <div key={h.id} className={`im-toast im-toast--${h.art === "gut" ? "good" : h.art === "kritisch" ? "critical" : "info"}`}
              role={h.art === "kritisch" ? "alert" : "status"}>
              <span className="im-toast__icon"><Icon size={16} strokeWidth={1.5} aria-hidden="true" /></span>
              <span><b>{h.titel}</b>{h.text && <div className="im-small">{h.text}</div>}</span>
              {h.aktion && (
                <button type="button" className="im-btn im-btn--ghost im-btn--sm"
                  onClick={() => { h.aktion?.los(); weg(h.id); }}>{h.aktion.text}</button>
              )}
              <button type="button" className="im-btn im-btn--ghost im-btn--sm" aria-label="Hinweis schließen"
                onClick={() => weg(h.id)}><X size={14} strokeWidth={1.5} aria-hidden="true" /></button>
            </div>
          );
        })}
      </div>
      {abfrage && <SchutzabfrageDialog abfrage={abfrage} />}
    </Kontext.Provider>
  );
}

function SchutzabfrageDialog({ abfrage }: { abfrage: Schutzabfrage & { antwort: (ja: boolean) => void } }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  return (
    <dialog ref={ref} className="im-dialog--modal" aria-labelledby="im-schutz-titel"
      onCancel={(e) => { e.preventDefault(); abfrage.antwort(false); }} data-ersetzt={abfrage.ersetzt}>
      <div className="im-dialog">
        <h3 className="im-dialog__title" id="im-schutz-titel">{abfrage.titel}</h3>
        <div style={{ whiteSpace: "pre-line" }}>{abfrage.text}</div>
        <div className="im-dialog__actions">
          <button type="button" className="im-btn im-btn--ghost" onClick={() => abfrage.antwort(false)}>Abbrechen</button>
          <button type="button" className={`im-btn ${abfrage.gefahr ? "im-btn--danger" : "im-btn--primary"}`} autoFocus
            onClick={() => abfrage.antwort(true)}>{abfrage.bestaetigen}</button>
        </div>
      </div>
    </dialog>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useRueckmeldung(): Rueckmeldung {
  const r = useContext(Kontext);
  if (!r) throw new Error("useRueckmeldung außerhalb von RueckmeldungProvider");
  return r;
}


/** Unten in der Ecke: was gerade im Hintergrund arbeitet (jeder Knopf, der länger als einen Moment braucht), mit
 *  laufender Zeit. Den Text setzt die Mutation über `meta: { laeuft: "…" }`, sonst „Wird bearbeitet …“. */
function Arbeit() {
  const laufend = useMutationState({
    filters: { status: "pending" },
    select: (m) => ({ id: m.mutationId, text: (m.options.meta as { laeuft?: string } | undefined)?.laeuft,
      seit: m.state.submittedAt }),
  });
  const [jetzt, setJetzt] = useState(() => Date.now());
  useEffect(() => {
    if (!laufend.length) return;
    const t = window.setInterval(() => setJetzt(Date.now()), 500);
    return () => window.clearInterval(t);
  }, [laufend.length]);
  const sichtbar = laufend.filter((m) => jetzt - m.seit >= 500);  // kurze Klicks blinken nicht auf
  return (
    <>
      {sichtbar.map((m) => {
        const s = Math.max(0, Math.round((jetzt - m.seit) / 1000));
        return (
          <div key={m.id} className="im-toast im-toast--arbeit" role="status" aria-label="Arbeitet">
            <span className="im-toast__icon"><LoaderCircle size={16} strokeWidth={1.75} className="im-dreht" aria-hidden="true" /></span>
            <span><b>{m.text ?? "Wird bearbeitet …"}</b>
              <div className="im-small">läuft seit {s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${s % 60} s`}</div></span>
            <span />
          </div>
        );
      })}
    </>
  );
}
